import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { v4 } from 'uuid';
import { ButtonWithLoader } from '../../../../../../design/buttons/ButtonWithLoader';
import { S_Modal } from '../../../../../../design/modals/modal/S_Modal';
import { EnableFor } from '../../../../../containers/EnableForRole';
import { useLoader } from '../../../../../helpers/useLoader';
import { InputStatus } from '../../../../../models/error.model';
import { ImageScopeName, isTextQuestion, QuestionType } from '../../../../annotation/question.model';
import { useProjectDetailsPermissions } from '../../../projectDetails.permissions';
import { IAnswerModel, QuestionForm } from '../components/QuestionForm';
import { IProjectDetailsToolsBl } from '../projectDetailsTools.bl';
import { IQuestion, MIN_ANSWERS_COUNT } from '../projectDetailsTools.models';

interface IProps {
  isAttribute: boolean;
  isReadonly: boolean;
  isLoading: boolean;
  question: IQuestion | undefined;
  questionsCount: number;
  selectedQuestionTypes: QuestionType[];
  existingQuestionsAndAttributes: { text: string; type: QuestionType }[];
  onSubmit: (model: IQuestion) => Promise<void>;
  onCancel: () => void;
  showModal: boolean;
  modalHeader: string;
}

interface IState {
  id: string;
  questionType: QuestionType;
  scopes: string[];
  questionText: string;
  isRequired: boolean;
  answers: IAnswerModel[];
  questionTextStatus: InputStatus;
  scopesStatus: InputStatus;
  answersStatus: InputStatus;
  isEdit: boolean;
  isUsed: boolean;
  isAttribute: boolean;
  order: number;
}

const questionTypes = Object.values(QuestionType);

const getDefaultTrueFalseAnswers = (): IAnswerModel[] => [
  { text: 'Yes', status: InputStatus.empty(), id: v4() },
  { text: 'No', status: InputStatus.empty(), id: v4() },
];

const getDefaultStatusPassFailAnswers = (): IAnswerModel[] => [
  { text: 'Good', status: InputStatus.empty(), id: v4() },
  { text: 'Bad', status: InputStatus.empty(), id: v4() },
];

const getDefaultSetTrainTestAnswers = (): IAnswerModel[] => [
  { text: 'Train', status: InputStatus.empty(), id: v4() },
  { text: 'Test', status: InputStatus.empty(), id: v4() },
];

const getInitialState = (props: IProps): IState => {
  const { question, isAttribute } = props;

  if (question) {
    return {
      isAttribute,
      id: question.id,
      questionType: question.type,
      scopes: question.scopes,
      questionText: question.questionText,
      isRequired: question.isRequired,
      answers: question.answers.map((x) => ({ ...x, status: InputStatus.empty() })),
      questionTextStatus: InputStatus.empty(),
      answersStatus: InputStatus.empty(),
      scopesStatus: InputStatus.empty(),
      isEdit: true,
      isUsed: question.isUsed,
      order: question.order,
    };
  }

  return {
    isAttribute,
    id: v4(),
    questionType: QuestionType.TRUE_FALSE,
    scopes: isAttribute ? [] : [ImageScopeName],
    questionText: '',
    isRequired: false,
    answers: getDefaultTrueFalseAnswers(),
    questionTextStatus: InputStatus.empty(),
    answersStatus: InputStatus.empty(),
    scopesStatus: InputStatus.empty(),
    isEdit: false,
    isUsed: false,
    order: props.questionsCount,
  };
};

export const QuestionModalContainer = observer((props: IProps): ReactElement => {
  const projectDetailsToolsBl = useInjection(IProjectDetailsToolsBl.$);
  const permissions = useProjectDetailsPermissions();

  const { t } = useTranslator();
  const { withLoaderAsync } = useLoader();

  const initialState = getInitialState(props);
  const [id, setId] = useState(initialState.id);
  const [questionType, setQuestionType] = useState(initialState.questionType);
  const [scopes, setScopes] = useState(initialState.scopes);
  const [questionText, setQuestionText] = useState(initialState.questionText);
  const [isRequired, setIsRequired] = useState(initialState.isRequired);
  const [answers, setAnswers] = useState(initialState.answers);
  const [questionTextStatus, setQuestionTextStatus] = useState(initialState.questionTextStatus);
  const [scopesStatus, setScopesStatus] = useState(initialState.scopesStatus);
  const [answersStatus, setAnswersStatus] = useState(initialState.answersStatus);
  const [isEdit, setIsEdit] = useState(initialState.isEdit);
  const [isUsed, setIsUsed] = useState(initialState.isUsed);
  const [isAttribute, setIsAttribute] = useState(initialState.isAttribute);
  const [order, setOrder] = useState(initialState.order);

  useEffect(() => {
    if (props.showModal) {
      const newState = getInitialState(props);
      setId(newState.id);
      setQuestionType(newState.questionType);
      setScopes(newState.scopes);
      setQuestionText(newState.questionText);
      setIsRequired(newState.isRequired);
      setAnswers(newState.answers);
      setQuestionTextStatus(newState.questionTextStatus);
      setScopesStatus(newState.scopesStatus);
      setAnswersStatus(newState.answersStatus);
      setIsEdit(newState.isEdit);
      setIsUsed(newState.isUsed);
      setIsAttribute(newState.isAttribute);
      setOrder(newState.order);
    }
  }, [props.showModal]);

  const handleQuestionTypeChange = (newQuestionType: QuestionType): void => {
    let newAnswers: IAnswerModel[] = answers;
    let newQuestionText: string = questionText;

    if (newQuestionType === QuestionType.TRUE_FALSE) {
      newAnswers = getDefaultTrueFalseAnswers();
    } else if (newQuestionType === QuestionType.STATUS_GOOD_BAD) {
      newAnswers = getDefaultStatusPassFailAnswers();
      newQuestionText = t('default_status_question');
    } else if (newQuestionType === QuestionType.SET_TRAIN_TEST) {
      newQuestionText = t('default_set_question');
      newAnswers = getDefaultSetTrainTestAnswers();
    } else if (isTextQuestion(newQuestionType) || questionType === QuestionType.TRUE_FALSE) {
      newAnswers = [];
    }

    setQuestionText(newQuestionText);
    setQuestionType(newQuestionType);
    setAnswers(newAnswers);
    setAnswersStatus(InputStatus.empty());
    setQuestionTextStatus(InputStatus.empty());
  };

  const handleQuestionTextValidationAsyncDebounced = useDebouncedCallback(async (text) => {
    await handleQuestionTextValidationAsync(text);
  }, 300);

  const handleQuestionTextChange = (text: string): void => {
    setQuestionText(text);
    void handleQuestionTextValidationAsyncDebounced(text);
  };

  const handleSubmitAsync = async (): Promise<void> => {
    await withLoaderAsync(async () => {
      if (!(await validateFormAsync())) {
        return;
      }

      const newQuestion = {
        id,
        type: questionType,
        questionText: questionText.trim(),
        isRequired,
        answers: answers.filter((x) => x.text),
        scopes,
        isUsed,
        order,
      };

      await props.onSubmit(newQuestion);
    }, 'submit-question-form-button');
  };

  const toggleIsRequiredStatus = (): void => {
    setIsRequired(!isRequired);
  };

  const handleAnswerAdd = (): void => {
    const newAnswersStatus = answers.length + 1 >= MIN_ANSWERS_COUNT ? InputStatus.empty() : answersStatus;
    setAnswers([...answers, { id: v4(), text: '', status: InputStatus.empty() }]);
    setAnswersStatus(newAnswersStatus);
  };

  const handleAnswerChange = (text: string, id: string): void => {
    const newAnswers = answers.slice();
    const answer = newAnswers.find((x) => x.id === id);
    if (answer) {
      answer.text = text;
      validateAnswers(newAnswers);
    }
  };

  const handleAnswerRemove = (id: string): void => {
    const newAnswers = answers.filter((x) => x.id !== id);
    const newAnswersStatus = getAnswersSummaryStatus(newAnswers);
    validateAnswers(newAnswers);
    setAnswers(newAnswers);
    setAnswersStatus(newAnswersStatus);
  };

  const handleQuestionTextValidationAsync = async (questionText: string): Promise<boolean> => {
    const newQuestionTextStatus: InputStatus = InputStatus.valid();

    if (questionText.trim() === '') {
      newQuestionTextStatus.addErrorCode('field_cant_be_empty');
    } else if (!(await projectDetailsToolsBl.isUniqueQuestionTextAsync(questionText, id, isAttribute))) {
      newQuestionTextStatus.addErrorCode('QUESTION_TEXT_IS_NOT_UNIQUE');
    }

    setQuestionTextStatus(newQuestionTextStatus);
    return newQuestionTextStatus.isValid === true;
  };

  const handleAnswersSummaryValidation = (): boolean => {
    const newAnswersStatus = getAnswersSummaryStatus(answers);
    setAnswersStatus(newAnswersStatus);
    return newAnswersStatus.isValid === true;
  };

  const validateFormAsync = async (): Promise<boolean> => {
    const isQuestionValid = await handleQuestionTextValidationAsync(questionText);
    const areAnswersValid = validateAnswers(answers);
    const areScopesValid = handleScopeValidation(scopes);
    const questionIsOpenOrAnswersSummaryIsValid = isTextQuestion(questionType) || handleAnswersSummaryValidation();

    return isQuestionValid && areAnswersValid && areScopesValid && questionIsOpenOrAnswersSummaryIsValid;
  };

  const validateAnswers = (answers: IAnswerModel[]): boolean => {
    answers.forEach((a: IAnswerModel) => {
      const answerStatus = InputStatus.valid();
      if (a.text.trim() === '') {
        answerStatus.addErrorCode('field_cant_be_empty');
      }
      if (answers.filter((b) => a.text === b.text).length > 1) {
        answerStatus.addErrorCode('answer_text_cant_duplicate');
      }
      a.status = answerStatus;
    });

    setAnswers(answers);

    return answers.filter((a) => !a.status.isValid).length === 0;
  };

  const isValidForm = (): boolean => {
    return (
      questionTextStatus.errorCodes.length === 0 &&
      (questionType === QuestionType.TRUE_FALSE || isTextQuestion(questionType) || answers.every((a) => a.status.isValid !== false)) &&
      answersStatus.isValid !== false
    );
  };

  const getAnswersSummaryStatus = (answers: IAnswerModel[]): InputStatus => {
    return answers.length >= MIN_ANSWERS_COUNT ? InputStatus.valid() : new InputStatus(false, ['at_least_two_answers_are_required']);
  };

  const handleScopeValidation = (scopes: string[]): boolean => {
    const newScopesStatus: InputStatus = InputStatus.valid();
    if (scopes.length === 0) {
      newScopesStatus.addErrorCode('scope_is_required');
    }
    setScopesStatus(newScopesStatus);
    return newScopesStatus.isValid === true;
  };

  const handleScopeChange = (scopes: string[] | null): void => {
    setScopes(scopes ?? []);
    setScopesStatus(InputStatus.empty());
  };

  return (
    <S_Modal
      isOpen={props.showModal}
      toggle={props.onCancel}
      headerText={props.modalHeader}
      buttons={
        <>
          <Button
            color='neutral'
            variant='outlined'
            onClick={props.onCancel}
          >
            {t('cancel')}
          </Button>
          {!isEdit && (
            <EnableFor hasPermission={permissions.canAddQuestion()}>
              <ButtonWithLoader
                onClick={handleSubmitAsync}
                disabled={!isValidForm()}
                loaderKey='submit-question-form-button'
              >
                {t('confirm')}
              </ButtonWithLoader>
            </EnableFor>
          )}
          {isEdit && (
            <>
              {!props.isReadonly && (
                <EnableFor hasPermission={permissions.canAddQuestion()}>
                  <ButtonWithLoader
                    onClick={handleSubmitAsync}
                    disabled={!isValidForm()}
                    loaderKey='submit-question-form-button'
                  >
                    {t('confirm')}
                  </ButtonWithLoader>
                </EnableFor>
              )}
            </>
          )}
        </>
      }
    >
      <QuestionForm
        questionTypes={questionTypes}
        id={id}
        answers={answers}
        isRequired={isRequired}
        questionText={questionText}
        questionType={questionType}
        selectedQuestionTypes={props.selectedQuestionTypes}
        onQuestionTypeChange={handleQuestionTypeChange}
        onQuestionTextChange={handleQuestionTextChange}
        onRequiredChange={toggleIsRequiredStatus}
        onAnswerAdd={handleAnswerAdd}
        onAnswerChange={handleAnswerChange}
        onAnswerRemove={handleAnswerRemove}
        questionTextStatus={questionTextStatus}
        answersStatus={answersStatus}
        isValid={isValidForm()}
        isReadonly={props.isReadonly}
        isInUse={isUsed}
        annotationTypes={projectDetailsToolsBl.store.annotationTypes}
        onScopeChange={handleScopeChange}
        scopes={scopes}
        scopesStatus={scopesStatus}
        isAttribute={isAttribute}
      />
    </S_Modal>
  );
});
